import React, {FunctionComponent, useState} from "react";
import {Box, Button, Divider, Stack, Typography} from "@mui/material";
import {
    ACard,
    AToggleButtonGroup,
    Period,
    PeriodSelector,
    PeriodSelectorGroupBy
} from "@atiautomacao/ati-ui-library";
import IconButton from "@mui/material/IconButton";
import SkidComparisonChart from "../SkidComparisonChart/SkidComparisonChart";
import GenerationRateChart from "../GenerationRateChart/GenerationRateChart";
import GenerationCorrectedRateChart from "../GenerationRateChart/GenerationCorrectedRateChart";
import ColorInformation, { NewHtmlTooltip } from "../../../../Utils/ColorInformation";
import { ChangeCircleOutlined } from "@mui/icons-material";

const GenerationRateAndComparisonSkids: FunctionComponent<{ powerPlant: any; }> = ({powerPlant}) => {
    const [prValue, setPrValue] = React.useState("0");

    const initialSkidComparisonPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };
    const initialGenerationRatePeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };

    const generatedEnergyCorrectedPeriod: Period = {
        groupBy: "week",
        fromDateTime: new Date(),
        toDateTime: new Date()
    };

    const SkidComparisonGroupsInterval = {
        day: {date: new Date(), startTime: "00:00:00", endTime: "23:59:59"},
        week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6},
        month: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 30},
        year: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 11},
    };
    const skidComparisonGroups : PeriodSelectorGroupBy[] = ["day", "week", "month", "year"];

    const GenerationRateGroupsInterval={
        day: {date: new Date(), startTime: "00:00:00", endTime: "23:59:59"},
        week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6},
        month: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 30},
        year: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 11},
    };

    const GeneratedEnergyCorrectedInterval={
        day: {date: new Date(), startTime: "00:00:00", endTime: "23:59:59"},
        week: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 6},
        month: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 30},
        year: {startTime: "00:00:00", endTime: "23:59:59", startInterval: 11},
    };

    const generationRateGroups: PeriodSelectorGroupBy[] = ["day", "week", "month", "year"];
    const generationRateCorrectedGroups: PeriodSelectorGroupBy[] = ["day", "week", "month", "year"];



    const [period, setPeriod] = useState<Period>(prValue === "0" ? initialSkidComparisonPeriod : prValue === "1" ? initialGenerationRatePeriod : generatedEnergyCorrectedPeriod);


    // SGD-3375: respeita o groupBy escolhido pelo usuário. Antes o handler sobrescrevia o groupBy
    // de volta pro inicial a cada clique, revertendo Dia/31 Dias/12 Meses para 7 dias.
    const handleTabChange = (period: Period) => {
        setPeriod(period);
    }

    const handleChangeChart = (event: React.MouseEvent<HTMLElement>, value: string | null) => {
        setPrValue(value ?? "0");
    };


    return (
        <ACard
            key={`chart-power-energy-comparison`}
            title="Comparação de Skids"
            ariaLabel={"dashboard-station-comparison-skid-chart"}
            styleProps={{
                cardStyle: { height: 463 },
                subTitleStyle: {display: 'none'},
                contentStyle: { padding: '0 16px', height: 408, alignItems: 'center' },
                footerStyle: {paddingTop: 40}
            }}
            headerControlsPosition={"header"}
            headerControls={
                <Stack direction="row" spacing={2}>
                    <PeriodSelector
                        styleProps={{base: {flexDirection: "row", display: "flex", alignItems: "center", padding: '8px 0'}}}
                        mode="hybrid"
                        hideDatetime={false}
                        disableHourSelector={true}
                        inputFormat={'dd/MM/yyyy'}
                        hideGroup={false}
                        periodInterval={prValue === "0" ? SkidComparisonGroupsInterval : prValue === "1"? GenerationRateGroupsInterval : GeneratedEnergyCorrectedInterval}
                        period={period}
                        onChange={handleTabChange}
                        groupByOptions={prValue === "0" ? skidComparisonGroups : prValue === "1"? generationRateGroups :  generationRateCorrectedGroups}
                    />
                </Stack>
            }
            headerActions={
                <Stack direction="column" spacing={"8px"}>
                    <Box sx={{ display: 'flex',alignItems: 'center', justifyContent: 'space-between', padding: '0 12px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', paddingRight: '32px' }}>
                            <IconButton sx={{borderRadius: '4px'}} size="small" onClick={() => setPrValue("0")}>
                                <ChangeCircleOutlined  sx={{marginRight: '12px'}}/>
                                <Typography variant="body2">Alterar dado de geração</Typography>

                            </IconButton>

                        </Box>
                        <AToggleButtonGroup
                            styleProps={{
                                toggleButtonGroup: {
                                    paddingLeft: '4px',
                                    height: '30px',
                                },
                                toggleButton: {
                                    height: '30px',
                                },
                                typography: {
                                    fontSize: '14px',
                                },
                            }}
                            options={[
                                { value: "0", label: 'Energia' },
                                { value: "1", label: 'Taxa' },
                                { value: "2", label: 'Corrigida' }
                            ]}
                            // size="{{size | downcase}}"
                            value={prValue}
                            exclusive
                            onChange={handleChangeChart}

                        />
                    </Box>
                    <Divider />
                </Stack>
            }
            InformationTooltip={
                prValue !== "0" ? (
                    <NewHtmlTooltip
                        style={{ alignItems: 'center', display: 'flex', padding: '8px' }}
                        title={
                            <ColorInformation />
                        }>
                    <Button>HTML</Button>
                </NewHtmlTooltip>
                ) : null
            }
        >
            { prValue === "0" ?
                <SkidComparisonChart period={period} powerPlant = {powerPlant}/>
                : prValue === "1" ?
                    <GenerationRateChart period={period} powerPlant = {powerPlant}/>
                    :
                    <GenerationCorrectedRateChart period={period} powerPlant = {powerPlant}/>
            }
        </ACard>
    )

}
export default GenerationRateAndComparisonSkids;